import { BidderSeat } from '@pubstack/common/src/bidder-seat'
import { BidderCatalog } from '@pubstack/common/src/bidderCatalog'
import { MEDIA_TYPES, MediaType } from '@pubstack/common/src/media-types'
import { cn } from '@pubstack/ui/utils/cn'
import { useAtomValue } from 'jotai'
import { FunctionComponent } from 'react'
import { BidderIdentity } from '~/components/catalog/BidderIdentity'
import { Icon } from '~/components/Icon'
import TableCell from '~/components/table/TableCell'
import TableRow from '~/components/table/TableRow'
import { Toggle } from '~/components/Toggle'
import { Tooltip } from '~/components/Tooltip'
import { SeatTypeChip } from '~/modules/pbs/integrations/bidder-seats/SeatTypeChip'
import { configurationState } from '~/state'
import { SelectedBidderSeats, isSeatEnabled, mediaTypeState, supportedMediaTypesOf } from './bidderRuleSections'

type Props = {
  seat: BidderSeat
  catalogBidders: BidderCatalog[]
  selected: SelectedBidderSeats
  onToggleSeat: (seat: BidderSeat, enabled: boolean) => void
  onToggleMediaType: (seat: BidderSeat, mediaType: MediaType) => void
}

/**
 * One seat of the rule: its identity and integration, its media types, and its toggle at the end of
 * the row — the seat itself leads, the way a bidder's name leads its card in the catalog, and the
 * toggle trails it the way a row's own switch does everywhere else the app draws one.
 *
 * Every media type is drawn on every row so the columns line up down the list; one the bidder
 * cannot serve is greyed out with the reason a hover away, rather than omitted — a hole in the row
 * reads as a rendering accident, not as an incompatibility.
 */
export const BidderRuleSeatRow: FunctionComponent<Props> = ({ seat, catalogBidders, selected, onToggleSeat, onToggleMediaType }) => {
  const { STATIC_ASSETS_CDN } = useAtomValue(configurationState)
  const compatible = supportedMediaTypesOf(seat, catalogBidders)
  const isEnabledSeat = isSeatEnabled(seat, selected)
  // Falls back to the code: a seat can outlive its catalog entry, and an empty cell would
  // read as a seat with no bidder at all. Same fallback as the seat list.
  const displayName = catalogBidders.find((bidder) => bidder.id === seat.bidderCode)?.displayName ?? seat.bidderCode

  return (
    <TableRow>
      <TableCell>
        <span className="flex min-w-0 items-center gap-2">
          <BidderIdentity className="min-w-0 font-medium" baseCDNUrl={STATIC_ASSETS_CDN} code={seat.bidderCode} name={seat.name} bidderName={displayName} />
          <SeatTypeChip seat={seat} />
        </span>
      </TableCell>

      <TableCell>
        <span className="truncate text-slate-400">{displayName}</span>
      </TableCell>

      <TableCell>
        <span className="flex items-center gap-2">
          {MEDIA_TYPES.map((mediaType) => {
            if (!compatible.includes(mediaType)) {
              return (
                <Tooltip key={mediaType} title={`${seat.bidderCode} does not support ${mediaType} inventory.`}>
                  <span className="flex size-8 cursor-not-allowed items-center justify-center rounded-full border border-slate-200 text-slate-300">
                    <Icon name={mediaType} />
                  </span>
                </Tooltip>
              )
            }
            const isEnabled = mediaTypeState(seat, mediaType, selected, catalogBidders) === 'enabled'
            return (
              <Tooltip key={mediaType} title={mediaType}>
                <button
                  type="button"
                  onClick={() => onToggleMediaType(seat, mediaType)}
                  aria-label={mediaType}
                  aria-pressed={isEnabled}
                  className={cn(
                    'flex size-8 items-center justify-center rounded-full border transition-colors',
                    isEnabled ? 'border-king bg-king text-white' : 'border-king bg-white text-king hover:bg-slate-100'
                  )}
                >
                  <Icon name={mediaType} />
                </button>
              </Tooltip>
            )
          })}
        </span>
      </TableCell>

      <TableCell className="w-0 text-right">
        {/* Driven from `selected` and flipped against it, never from the hidden input's own checked
            state — that input is `display: none`, so its change event only ever fires through a
            wrapping label, and an uncontrolled copy of this row's state would drift the moment
            anything else edited the selection. */}
        <Toggle value={isEnabledSeat} onClick={() => onToggleSeat(seat, !isEnabledSeat)} aria-label={seat.name} trackingLabel="common-bidder-rules-seat-edit" />
      </TableCell>
    </TableRow>
  )
}
